import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../config/constants";
import { localizationMapping, evaluationPercentageKeys } from "../functions";
// Components
import { IndicatorDataDiv, IndicatorDataText, EvaluationDataDiv, Row, Box, Text } from "./styles";
import Loader from "../../loader";
import Axios from "../../../config/axios";
import PatientNavigation from "../../patientNavigation";
import MenuNavigation from "../sections/menuNavigation";
import Title from "../../title";
import DownloadReportPDF from "../sections/downloadReportPDF";
import LinePlot from "./sections/linePlot";

const Evaluations = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [isLoadingEvaluations, setIsLoadingEvaluations] = useState(true);
  const [firstEvaluations, setFirstEvaluations] = useState([]);
  const [evaluationDates, setEvaluationDates] = useState([]);
  const [evaluationKeys] = useState(["Delirium", "MechanicalVentilation", "Position", "Mobility", "Observations"]);

  // Set plot images
  const RASSPlotRef = useRef(null);
  const DyspnoeaPlotRef = useRef(null);
  const PainPlotRef = useRef(null);
  const AnxiousSymptomatologyPlotRef = useRef(null);
  const DepressiveSymptomsPlotRef = useRef(null);
  const SleepPlotRef = useRef(null);
  const superSessionsDataRef = useRef(null);
  const DeliriumRef = useRef(null);
  const MechanicalVentilationRef = useRef(null);
  const PositionRef = useRef(null);
  const MobilityRef = useRef(null);

  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetching evaluations
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/icu/get-evaluations/${patientId}`)
      .then(({ data }) => {
        // Getting evaluations by date
        let uniqueDates = new Set();
        let evaluationDates = data
          .filter((evaluation) => {
            const dateOnly = evaluation.EVALUATION_DATE.substring(0, 10);
            if (!uniqueDates.has(dateOnly)) {
              uniqueDates.add(dateOnly);
              return true;
            }
            return false;
          })
          .map((evaluation) => evaluation.EVALUATION_DATE.substring(0, 10));

        const firstEvaluations = evaluationDates
          .map((evaluationDate) => {
            return data.find((evaluation) => evaluation.EVALUATION_DATE.substring(0, 10) === evaluationDate);
          })
          .reverse();

        setEvaluations(data);
        setFirstEvaluations(firstEvaluations);
        setEvaluationDates(evaluationDates);
        setIsLoadingEvaluations(false);
      })
      .catch((error) => {
        console.log("Get evaluations: ", error);
      });

    return () => {
      setEvaluations([]);
      setFirstEvaluations([]);
      setEvaluationDates([]);
      setIsLoadingEvaluations(true);
    };
  }, [patientId]);

  const renderEvaluationsData = () => {
    const renderHeader = () => {
      return (
        <Row>
          <Box width="15%" background="#F2F2F2F2">
            <Text strong>{t("sessions")}</Text>
          </Box>
          {evaluationKeys.map((evaluationKey, i) => {
            return (
              <Box key={evaluationKey} width="17%" background="#F2F2F2F2" borderLeft={i === 0 && "2px solid #fff"}>
                <Text strong>{t(evaluationKey)}</Text>
              </Box>
            );
          })}
        </Row>
      );
    };

    const renderEvaluations = () => {
      return firstEvaluations.map((evaluation, i) => {
        return (
          <Row key={evaluation.EVALUATION_UNIT} borderBottom={i + 1 < evaluationDates.length && "2px solid #F2F2F2F2"}>
            <Box width="15%" alignItems="flex-start" background="#F2F2F2F2">
              <Text>{evaluationDates.length - i}</Text>
            </Box>
            <Box width="17%" alignItems="flex-start" background="#FFF">
              <Text>{t(localizationMapping(evaluation.Delirium))}</Text>
            </Box>
            <Box width="17%" alignItems="flex-start" background="#FFF">
              <Text>{t(localizationMapping(evaluation.MechanicalVentilation))}</Text>
            </Box>
            <Box width="17%" alignItems="flex-start" background="#FFF">
              <Text>{t(localizationMapping(evaluation.Position))}</Text>
            </Box>
            <Box width="17%" alignItems="flex-start" background="#FFF">
              <Text>{t(localizationMapping(evaluation.Mobility))}</Text>
            </Box>
            <Box width="17%" alignItems="flex-start" background="#FFF">
              <Text>{evaluation.Observations}</Text>
            </Box>
          </Row>
        );
      });
    };

    return (
      <EvaluationDataDiv ref={superSessionsDataRef}>
        {renderHeader()}
        {renderEvaluations()}
      </EvaluationDataDiv>
    );
  };

  const getValuePercentage = (key, value) => {
    const valueCount = firstEvaluations.reduce((count, firstEvaluation) => count + (firstEvaluation[key] === value ? 1 : 0), 0);
    const percentage = Math.round((valueCount / firstEvaluations.length) * 100);
    return !percentage ? "-" : `${percentage}%`;
  };

  const renderEvaluationsDataPercentage = () => {
    const keyRefs = {
      Delirium: DeliriumRef,
      MechanicalVentilation: MechanicalVentilationRef,
      Position: PositionRef,
      Mobility: MobilityRef,
    };
    return evaluationPercentageKeys.map((evaluation) => {
      const title = evaluation.key;
      const evaluationValues = evaluation.values;
      return (
        <EvaluationDataDiv key={title} ref={keyRefs[title]}>
          <Row background="#F2F2F2F2">
            <Box width="15%" />
            {evaluationValues.map((value, i) => {
              return (
                <Box key={value} width="17%" borderLeft={i === 0 && "2px solid #fff"}>
                  <Text strong>{t(localizationMapping(value))}</Text>
                </Box>
              );
            })}
          </Row>
          <Row background="#FFF">
            <Box width="15%" background="#F2F2F2F2">
              <Text strong>{t(title)}</Text>
            </Box>
            {evaluationValues.map((value) => {
              return (
                <Box key={value} width="17%">
                  <Text>{getValuePercentage(title, value)}</Text>
                </Box>
              );
            })}
          </Row>
        </EvaluationDataDiv>
      );
    });
  };

  return (
    <>
      {isLoadingEvaluations ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={"icu"} />
          <MenuNavigation />
          {!Boolean(evaluations.length) ? (
            <IndicatorDataDiv>
              <IndicatorDataText>{t("rgsicu_noEvaluationsMessage")}</IndicatorDataText>
            </IndicatorDataDiv>
          ) : (
            <>
              <Title title={`${t("reports").toUpperCase()}: ${t("evaluation").toUpperCase()}`} marginBottom="10px" />
              <DownloadReportPDF
                view={t("evaluation")}
                plotsRef={{
                  RASSPlotRef,
                  DyspnoeaPlotRef,
                  PainPlotRef,
                  AnxiousSymptomatologyPlotRef,
                  DepressiveSymptomsPlotRef,
                  SleepPlotRef,
                  superSessionsDataRef,
                  DeliriumRef,
                  MechanicalVentilationRef,
                  PositionRef,
                  MobilityRef,
                }}
              />
              <LinePlot
                ref={RASSPlotRef}
                evaluationDates={evaluationDates}
                evaluations={evaluations}
                title="RASS"
                min={-5}
                max={5}
                showLegend={false}
              />
              <LinePlot ref={DyspnoeaPlotRef} evaluationDates={evaluationDates} evaluations={evaluations} title="Dyspnoea" hasTwoLines />
              <LinePlot ref={PainPlotRef} evaluationDates={evaluationDates} evaluations={evaluations} title="Pain" hasTwoLines />
              <LinePlot
                ref={AnxiousSymptomatologyPlotRef}
                evaluationDates={evaluationDates}
                evaluations={evaluations}
                title="AnxiousSymptomatology"
                hasTwoLines
              />
              <LinePlot
                ref={DepressiveSymptomsPlotRef}
                evaluationDates={evaluationDates}
                evaluations={evaluations}
                title="DepressiveSymptoms"
                hasTwoLines
              />
              <LinePlot
                ref={SleepPlotRef}
                evaluationDates={evaluationDates}
                evaluations={evaluations}
                title="Sleep"
                min={0}
                max={10}
                showLegend={false}
              />
              {renderEvaluationsData()}
              {renderEvaluationsDataPercentage()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Evaluations;
